import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { dynamicCacheKey } from 'dashboard/store/modules/wgptAutomationRegistry';
import { getFieldVariables } from '../../../../helpers/automationUtils';
import {
  skipValidation,
  validateField,
} from '../../../../helpers/fieldValidation';

function validateActionStepFormFields(step, actionRegistry, context) {
  const actionAttributes = step.attributes || {};
  const formFields = actionRegistry.form_fields || [];
  formFields.forEach(field => {
    const fieldName = field.name;
    const actionAttributeValue = actionAttributes[fieldName] ?? field.default;
    if (skipValidation(field, actionAttributes, context)) return;
    validateField(field, actionAttributeValue, context);
  });
}

function validateActionStepOutputMappingFields(step, actionRegistry, context) {
  const actionOutputMapping = step.output_mapping || {};
  const outputMappingFields = actionRegistry.output_mapping_fields || [];
  outputMappingFields.forEach(field => {
    const fieldName = field.name;
    const actionOutputMappingValue =
      actionOutputMapping[fieldName] ?? field.default;
    validateField(field, actionOutputMappingValue, context);
  });
}

const actionValidation = params =>
  helpers.withParams(
    {
      value: params,
    },
    step => {
      const { actionRegistry, context } = params;
      if (!actionRegistry) return true;
      try {
        validateActionStepFormFields(step, actionRegistry, context);
        validateActionStepOutputMappingFields(step, actionRegistry, context);
        return true;
      } catch (error) {
        return false;
      }
    }
  );

function validateJumpToStepAction(step, getParentSteps, updateStep) {
  if (step.action !== 'jump_to_step') return;

  const targetStepId = step.attributes?.step_id;
  if (!targetStepId) {
    throw new Error(`'step_id' is required for jump_to_step action.`);
  }
  const parentSteps = getParentSteps(step.id);
  const parentStepIds = parentSteps.map(({ id }) => id);
  if (parentStepIds.includes(targetStepId)) return;

  updateStep({
    attributes: { ...step.attributes, step_id: null },
  });
  throw new Error(`'step_id' ${targetStepId} is not a valid step to jump to.`);
}

const jumpToStepActionValidation = params =>
  helpers.withParams(
    {
      value: params,
    },
    step => {
      const { getParentSteps, updateStep } = params;
      try {
        validateJumpToStepAction(step, getParentSteps, updateStep);
        return true;
      } catch (error) {
        return false;
      }
    }
  );

const getVariables = (outputMapping, outputMappingFields) => {
  const variables = getFieldVariables(outputMapping, outputMappingFields);
  const variableNames = variables.map(({ name }) => name);
  return variableNames;
};

const variablesValidation = params =>
  helpers.withParams(
    {
      value: params,
    },
    step => {
      try {
        const [actionRegistry, inheritedVariables] = params;
        const outputMappingFields = actionRegistry?.output_mapping_fields ?? [];
        const outputMapping = step.output_mapping ?? {};
        const outputMappingVariables = getVariables(
          outputMapping,
          outputMappingFields
        );
        const invalidVariables = outputMappingVariables.filter(
          outputMappingVariable => {
            if (!outputMappingVariable) return false;
            if (!/^[a-zA-Z0-9_]+$/.test(outputMappingVariable)) return true;
            return false;
          }
        );
        if (invalidVariables.length > 0) {
          throw new Error(
            `Variables ${invalidVariables.join(
              ', '
            )} must not contain special characters.`
          );
        }
        const duplicateVariables = outputMappingVariables.filter(
          outputMappingVariable => {
            const matched = inheritedVariables.filter(inheritedVariable => {
              if (inheritedVariables.wrapper) return false;
              return inheritedVariable.name === outputMappingVariable;
            });
            return matched.length > 0;
          }
        );
        if (duplicateVariables.length > 0) {
          throw new Error(
            `Variables ${duplicateVariables.join(', ')} cannot be used anymore.`
          );
        }
        return true;
      } catch (error) {
        return false;
      }
    }
  );
export default {
  setup() {
    return { v$: useVuelidate() };
  },
  computed: {
    ...mapGetters({
      getParentSteps: 'wgptAutomationBuilder/getParentSteps',
      customObjectsGetter: 'wgptAutomationRegistry/getCustomObjects',
      dynamicOptionsGetter: 'wgptAutomationRegistry/getDynamicOptions',
      dynamicFlagsGetter: 'wgptAutomationRegistry/getDynamicFlags',
    }),
    resolvedFieldOptions() {
      // Tracks when model-based field options load in the store.
      // Included in validations params so Vuelidate re-evaluates.
      const result = {};
      const registry = this.actionRegistry;
      if (!registry) return result;
      const collectModels = fields => {
        (fields || []).forEach(field => {
          if (field.model) {
            const data = this.dynamicOptionsGetter(field.model);
            if (data) result[`dm:${field.model}`] = data;
          }
          if (field.controls) collectModels(field.controls);
        });
      };
      collectModels(registry.form_fields);
      collectModels(registry.output_mapping_fields);
      return result;
    },
    resolvedDynamicFlags() {
      // Tracks when dynamic flags load in the store.
      // Included in validations params so Vuelidate re-evaluates.
      const result = {};
      const registry = this.actionRegistry;
      if (!registry) return result;
      const attributes = this.step?.attributes || {};
      const collectFlags = (fields, attrs) => {
        (fields || []).forEach(field => {
          (field.show_if || []).forEach(condition => {
            if (!condition.dynamic_flag) return;
            const params = {};
            (condition.params || []).forEach(p => {
              params[p] = attrs[p];
            });
            if (
              (condition.params || []).some(
                p => !params[p] && params[p] !== false
              )
            )
              return;
            const cacheKey = dynamicCacheKey(condition.dynamic_flag, params);
            const data = this.dynamicFlagsGetter(cacheKey);
            if (data) result[cacheKey] = data;
          });
          if (field.controls) {
            const groupAttrs =
              field.type === 'group' ? attrs[field.name] || {} : attrs;
            collectFlags(field.controls, groupAttrs);
          }
        });
      };
      collectFlags(registry.form_fields, attributes);
      return result;
    },
  },
  watch: {
    actionRegistry: {
      handler(registry) {
        this.$nextTick(() => {
          if (this.v$.step) {
            this.v$.step.$touch();
          }
        });
        if (!registry) return;
        // Eagerly load dynamic options for all model-based form fields
        const loadModels = fields => {
          (fields || []).forEach(field => {
            if (
              field.model &&
              !field.params?.length &&
              !this.dynamicOptionsGetter(field.model)
            ) {
              this.$store.dispatch(
                'wgptAutomationRegistry/getDynamicOptions',
                field.model
              );
            }
            if (field.controls) loadModels(field.controls);
          });
        };
        loadModels(registry.form_fields);
        loadModels(registry.output_mapping_fields);

        // Eagerly load dynamic flags for show_if conditions
        const attributes = this.step?.attributes || {};
        const loadFlags = (fields, attrs) => {
          (fields || []).forEach(field => {
            (field.show_if || []).forEach(condition => {
              if (!condition.dynamic_flag) return;
              const params = {};
              (condition.params || []).forEach(p => {
                params[p] = attrs[p];
              });
              if (
                (condition.params || []).some(
                  p => !params[p] && params[p] !== false
                )
              )
                return;
              const cacheKey = dynamicCacheKey(condition.dynamic_flag, params);
              if (this.dynamicFlagsGetter(cacheKey)) return;
              this.$store.dispatch('wgptAutomationRegistry/getDynamicFlags', {
                flagName: condition.dynamic_flag,
                params,
              });
            });
            if (field.controls) {
              const groupAttrs =
                field.type === 'group' ? attrs[field.name] || {} : attrs;
              loadFlags(field.controls, groupAttrs);
            }
          });
        };
        loadFlags(registry.form_fields, attributes);
      },
      immediate: true,
    },
    resolvedFieldOptions: {
      handler() {
        this.$nextTick(() => {
          if (this.v$.step) {
            this.v$.step.$touch();
          }
        });
      },
      immediate: true,
    },
    resolvedDynamicFlags: {
      handler() {
        this.$nextTick(() => {
          if (this.v$.step) {
            this.v$.step.$touch();
          }
        });
      },
    },
  },
  validations() {
    const context = {
      variables: this.variables,
      getCustomObjects: this.customObjectsGetter,
      getDynamicOptions: this.dynamicOptionsGetter,
      getDynamicFlags: this.dynamicFlagsGetter,
    };
    const actionValidationParams = {
      actionRegistry: this.actionRegistry,
      context,
      resolvedFieldOptions: this.resolvedFieldOptions,
      resolvedDynamicFlags: this.resolvedDynamicFlags,
    };
    const jumpToStepActionValidationParams = {
      getParentSteps: this.getParentSteps,
      updateStep: this.updateStep,
    };
    return {
      step: {
        actionValidation: actionValidation(actionValidationParams),
        variablesValidation: variablesValidation([
          this.actionRegistry,
          this.variables,
        ]),
        jumpToStepActionValidation: jumpToStepActionValidation(
          jumpToStepActionValidationParams
        ),
      },
    };
  },
  methods: {
    updateStep() {
      this.$emit('stepDelete', this.step.id);
    },
  },
};
